"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { PerksSection } from "@/features/home/ui/components/PerksSection";
import { Pagination } from "@/features/products/ui/components/Pagination";
import { Link, usePathname, useRouter } from "@/i18n/navigation";
import { NotFound } from "@/lib/client/components/NotFound";
import { getBreadcrumbHref, type IBreadcrumbsNav } from "@/lib/shared/types/breadcrumbs";
import type { IPaginatedResult } from "@/lib/shared/types/pagination";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Container,
  Grid,
  HStack,
  LoadingOverlay,
  Section,
} from "@/ui";
import type { GiftSet } from "../../shared/types";
import { useGiftSetsGridQuery } from "../hooks/useGiftSetsGridQuery";
import { GiftSetCard } from "./GiftSetCard";

interface GiftSetsGridContainerProps {
  giftSets: IPaginatedResult<GiftSet>;
  breadcrumbsNav: IBreadcrumbsNav;
}

export const GiftSetsGridContainer = (props: GiftSetsGridContainerProps) => {
  const { giftSets, breadcrumbsNav } = props;
  const navigationT = useTranslations("navigation");
  const [page, setPage] = useState(1);
  const router = useRouter();
  const pathname = usePathname();

  const { data: queryData, isFetching } = useGiftSetsGridQuery({
    page,
    initialData: giftSets,
  });

  const pageChangeRequestedRef = useRef(false);
  const level1Href = getBreadcrumbHref(breadcrumbsNav, "level1");
  const level2Href = getBreadcrumbHref(breadcrumbsNav, "level2");
  const level3Href = getBreadcrumbHref(breadcrumbsNav, "level3");

  const giftSetsData = queryData ?? giftSets;

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    const initialPage = currentParams.get("page");
    if (initialPage) {
      setPage(Number.parseInt(initialPage));
    }
  }, []);

  const onPageChange = (nextPage: number) => {
    pageChangeRequestedRef.current = true;
    setPage(nextPage);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set("page", String(nextPage));
    router.replace(`${pathname}?${currentParams.toString()}`, {
      scroll: false,
    });
  };

  useEffect(() => {
    if (pageChangeRequestedRef.current && !isFetching && giftSetsData?.docs) {
      pageChangeRequestedRef.current = false;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isFetching, giftSetsData?.docs]);

  return giftSetsData?.docs ? (
    <>
      <LoadingOverlay isLoading={isFetching} />
      <Container>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
          {navigationT(breadcrumbsNav.current)}
        </h1>
        <HStack className={"justify-between"}>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/">{navigationT("home")}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbsNav.level1 && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {level1Href ? (
                      <BreadcrumbLink asChild>
                        <Link href={level1Href}>{navigationT(breadcrumbsNav.level1)}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{navigationT(breadcrumbsNav.level1)}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </>
              )}
              {breadcrumbsNav.level2 && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {level2Href ? (
                      <BreadcrumbLink asChild>
                        <Link href={level2Href}>{navigationT(breadcrumbsNav.level2)}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{navigationT(breadcrumbsNav.level2)}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </>
              )}
              {breadcrumbsNav.level3 && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {level3Href ? (
                      <BreadcrumbLink asChild>
                        <Link href={level3Href}>{navigationT(breadcrumbsNav.level3)}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage>{navigationT(breadcrumbsNav.level3)}</BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{navigationT(breadcrumbsNav.current)}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </HStack>
        <Section
          className="px-0 py-4"
          data-testid="gift-sets-grid"
        >
          {giftSetsData.docs.length > 0 ? (
            <Grid className="grid-cols-2 gap-x-2 gap-y-6 md:grid-cols-3 lg:grid-cols-4">
              {giftSetsData.docs.map((giftSet, index) => (
                <GiftSetCard
                  key={giftSet.id}
                  giftSet={giftSet}
                  index={index}
                />
              ))}
            </Grid>
          ) : (
            <NotFound />
          )}
        </Section>
        <Pagination
          pagination={{
            ...giftSetsData,
          }}
          onPageChange={onPageChange}
        />
        <PerksSection />
      </Container>
    </>
  ) : (
    <NotFound />
  );
};
